import { Component, computed, Signal, Input, Output, EventEmitter } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { groupBy } from 'lodash';
import { DsButtonVariants, DsModalService, DsModalVariant, Icons } from '@levelaccess/design-system';

import { INavMenuItem, NavigationContext, NavigationGroup, NavigationItem } from '../../services/navigation/nav-structure';
import { NavService } from '../../services/navigation/nav.service';
import { ExternalLinkService } from '../../services/external-link.service';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import { AppConfigService } from '../../services/app-config.service';
import { AccountSettingsModalComponent } from '../../pages/public/settings/account/modal/account-settings-modal.component';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../shared/interfaces/feature-flag.interface';

interface IVerticalMenu {
  key: string;
  title: string;
  items: INavMenuItem[];
}

const GROUP_LABELS: Record<NavigationGroup, string> = {
  [NavigationGroup.organization]: 'organization',
  [NavigationGroup.workspace]: 'workspace',
  [NavigationGroup.digital_asset]: 'digital_asset',
  [NavigationGroup.find_and_fix]: 'find_and_fix',
  [NavigationGroup.automated_tests]: 'automated_tests',
  [NavigationGroup.expert_audits]: 'expert_audits',
  [NavigationGroup.manage]: 'label_manage',
  [NavigationGroup.support]: 'support',
};

@Component({
  selector: 'vertical-navigation',
  templateUrl: './vertical-navigation.component.html',
  styleUrls: ['./vertical-navigation.component.scss'],
  standalone: false,
})
export class VerticalNavigationComponent {
  @Input() isCollapsed: boolean = false;
  @Input() showMobileAccountSection: boolean = false;
  @Output() menuItemClicked: EventEmitter<INavMenuItem> = new EventEmitter<INavMenuItem>();

  protected readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  protected readonly Icons: typeof Icons = Icons;

  public verticalMenus: Signal<IVerticalMenu[]>;
  protected accountMenuItems: Signal<INavMenuItem[]>;
  protected logoutItem: Signal<INavMenuItem | null>;

  private contextItems: Signal<INavMenuItem[]>;
  private globalItems: Signal<INavMenuItem[]>;
  private profileMenuItems: Signal<INavMenuItem[]>;

  constructor(
    private navService: NavService,
    private appConfigService: AppConfigService,
    private externalLinkService: ExternalLinkService,
    private dsModalService: DsModalService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.contextItems = toSignal(this.getContextItems(), { initialValue: [] });
    this.globalItems = toSignal(this.getGlobalItems(), { initialValue: [] });
    this.verticalMenus = computed(() => this.getVerticalMenus());

    this.profileMenuItems = toSignal(
      this.navService.getProfileMenuItems$(() => this.openAccountSettingsModal()),
      { initialValue: [] },
    );
    this.accountMenuItems = computed(() =>
      this.profileMenuItems().filter((item: INavMenuItem) => item.id !== NavigationItem.logout),
    );
    this.logoutItem = computed(
      () => this.profileMenuItems().find((item: INavMenuItem) => item.id === NavigationItem.logout) ?? null,
    );
  }

  private getVerticalMenus(): IVerticalMenu[] {
    const menuItems: INavMenuItem[] = [...this.contextItems(), ...this.globalItems()];
    const groupedItems = groupBy(menuItems, (item: INavMenuItem) => item.group);

    return Object.entries(groupedItems).map(([group, items]: [NavigationGroup, INavMenuItem[]]) => ({
      key: group,
      title: GROUP_LABELS[group],
      items: items,
    }));
  }

  private getContextItems(): Observable<INavMenuItem[]> {
    if (!this.appConfigService.isTenantEnabled()) {
      return this.getAdminItems();
    }

    return this.navService.currentNavigationContext$.pipe(
      switchMap((context: NavigationContext) => {
        switch (context) {
          case NavigationContext.tenant:
            return this.getTenantContextItems();
          case NavigationContext.workspace:
            return this.getWorkspaceContextItems();
          case NavigationContext.digitalProperty:
            return this.getDigitalPropertyContextItems();
          default:
            return of([]);
        }
      }),
    );
  }

  private getTenantContextItems(): Observable<INavMenuItem[]> {
    return this.navService.getTopLevelMenuItems$([NavigationItem.organization_dashboard]).pipe(
      map(([orgDashboardItem]: INavMenuItem[]) => {
        return this.prepareMenuItems(orgDashboardItem?.children ?? [], NavigationContext.tenant);
      }),
    );
  }

  private getWorkspaceContextItems(): Observable<INavMenuItem[]> {
    return this.navService.getTopLevelMenuItems$([NavigationItem.workspace]).pipe(
      map(([workspaceMenuItem]: [INavMenuItem]) => {
        return this.prepareMenuItems(workspaceMenuItem?.children ?? [], NavigationContext.workspace);
      }),
    );
  }

  private getDigitalPropertyContextItems(): Observable<INavMenuItem[]> {
    const workspaceShortcuts: NavigationItem[] = [NavigationItem.pdf];

    return combineLatest([
      this.navService.activeSecondLevelMenuItem$().pipe(
        map((item: INavMenuItem) => {
          return this.prepareMenuItems(item?.children ?? []);
        }),
      ),
      this.getWorkspaceContextItems().pipe(
        map((workspaceItems: INavMenuItem[]) => {
          return workspaceItems.filter((item: INavMenuItem) => workspaceShortcuts.includes(item.id));
        }),
      ),
    ]).pipe(
      map(([dpContextItems, workspaceItems]: [INavMenuItem[], INavMenuItem[]]) => {
        return [...dpContextItems, ...workspaceItems];
      }),
    );
  }

  private getAdminItems(): Observable<INavMenuItem[]> {
    return this.navService
      .getTopLevelMenuItems$([NavigationItem.administration, NavigationItem.internal_use_only])
      .pipe(map((items: INavMenuItem[]) => this.prepareMenuItems(items)));
  }

  private getGlobalItems(): Observable<INavMenuItem[]> {
    return combineLatest([
      this.featureFlagService.variation$(FeatureFlagCollection.globalOrgSettings, false),
      this.navService.currentNavigationContext$,
    ]).pipe(
      switchMap(([globalOrgSettingsEnabled, navigationContext]: [boolean, NavigationContext]) => {
        const itemIds: NavigationItem[] = [
          NavigationItem.level_ci,
          NavigationItem.academy,
          NavigationItem.help,
          NavigationItem.service_request,
        ];

        const isAdmin: boolean = !this.appConfigService.isTenantEnabled();
        if (globalOrgSettingsEnabled || navigationContext === NavigationContext.tenant || isAdmin) {
          itemIds.unshift(
            NavigationItem.evaluations,
            NavigationItem.organization_settings,
            NavigationItem.tools_and_integrations,
          );
        }

        return this.navService.getTopLevelMenuItems$(itemIds).pipe(map((items: INavMenuItem[]) => this.prepareMenuItems(items)));
      }),
    );
  }

  private prepareMenuItems(menuItems: INavMenuItem[], context?: NavigationContext): INavMenuItem[] {
    return menuItems
      .filter((item: INavMenuItem) => this.isMenuItemVisible(item) && this.isMenuItemForContext(item, context))
      .map((item: INavMenuItem) => ({
        ...item,
        externalLink: SharedCommonUtility.notNullish(item.externalLinkFn)
          ? item.externalLinkFn(this.externalLinkService)
          : item.externalLink,
        children: SharedCommonUtility.notNullishOrEmpty(item.children) ? this.prepareMenuItems(item.children, context) : [],
      }));
  }

  private isMenuItemVisible(item: INavMenuItem): boolean {
    return item.isAvailable && !item.isHidden && !item.displayedOutsideOfNavbar && !item.mobileOnly;
  }

  private isMenuItemForContext(item: INavMenuItem, context?: NavigationContext): boolean {
    return SharedCommonUtility.notNullish(context) ? NavService.getMenuItemContext(item) === context : true;
  }

  public onMenuItemClick(menuItem: INavMenuItem): void {
    this.menuItemClicked.emit(menuItem);
  }

  private openAccountSettingsModal(): void {
    this.dsModalService.open(AccountSettingsModalComponent, DsModalVariant.center);
  }
}
